// src/components/rpa/agent/GovernanceAgentChatLogic.js
//
// ⭐ NEW (Aug 2026) — the AI Governance Agent chat.
//
// Talks to POST /admin/rpa/agent/governance/command, which is a READ-ONLY
// analyst over token cost and guardrail data. It performs no AI calls and
// takes no actions, so this component has no approval gate and no
// destructive confirmations — by design. Operating the guardrails (resume
// after a hard kill, reset the daily budget) stays on the dashboard behind
// system_admin, per WISP §7.2.
//
// Every response carries { type, message, data }. `message` always stands
// alone, so an unknown type still renders as readable text rather than
// breaking the thread.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import './GovernanceAgentChatLogic.css';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const QUICK_ACTIONS = [
  { label: '📊 Status now',      prompt: 'What is our AI spend status right now?' },
  { label: '💸 Top cost drivers', prompt: 'Which features are costing the most this month?' },
  { label: '📈 Daily trend',      prompt: 'Show me spend by day for the last 30 days' },
  { label: '🛡️ Guardrails',       prompt: 'Have any guardrails activated recently?' },
  { label: '📄 Monthly report',   prompt: 'Generate the monthly report for the President' },
];

const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const num = (n) => (Number(n) || 0).toLocaleString();
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return '—'; }
};
const fmtDay = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', timeZone: 'UTC'
    });
  } catch { return d; }
};

// ── card renderers ──────────────────────────────────────────────────────────

const StatusCard = ({ d }) => {
  if (!d) return null;
  const pct = Math.min(100, Math.max(0, d.pctOfBudget ?? 0));
  const tone = d.hardKillActive ? 'critical' : pct >= 80 ? 'warn' : pct >= 50 ? 'notice' : 'ok';
  return (
    <div className={`gov-card gov-status gov-tone-${tone}`}>
      <div className="gov-status-head">
        <div>
          <div className="gov-big">{usd(d.spentTodayUSD ?? d.costUSD ?? 0)}</div>
          <div className="gov-sub">
            {d.dailyBudgetUSD ? `of $${d.dailyBudgetUSD} daily budget` : (d.window || '')}
          </div>
        </div>
        {d.dailyBudgetUSD != null && <div className="gov-pct">{pct}%</div>}
      </div>
      {d.dailyBudgetUSD != null && (
        <div className="gov-meter">
          <div className="gov-meter-fill" style={{ width: `${pct}%` }} />
          <span className="gov-meter-tick" style={{ left: '50%' }} title="50% soft alert" />
          <span className="gov-meter-tick" style={{ left: '80%' }} title="80% soft alert" />
        </div>
      )}
      <div className="gov-stats">
        {d.callsToday != null && <div><span>{num(d.callsToday)}</span>calls</div>}
        {d.calls != null && <div><span>{num(d.calls)}</span>calls</div>}
        {(d.tokensToday ?? d.totalTokens) != null && (
          <div><span>{num(d.tokensToday ?? d.totalTokens)}</span>tokens</div>
        )}
        {d.avgCostPerCall != null && <div><span>${d.avgCostPerCall}</span>avg/call</div>}
      </div>
      {d.hardKillActive && (
        <div className="gov-alert gov-alert-critical">
          ⛔ Hard kill active — agent AI calls suspended platform-wide. A system
          administrator must authorize resumption from the dashboard above.
        </div>
      )}
    </div>
  );
};

const BreakdownCard = ({ d }) => {
  if (!d?.rows?.length) return null;
  const max = Math.max(...d.rows.map(r => r.costUSD), 0.0001);
  const isAdmin = d.groupedBy === 'admin';
  return (
    <div className="gov-card">
      <div className="gov-card-title">
        {isAdmin ? 'Spend by account' : 'Spend by feature'} · {d.window}
      </div>
      <div className="gov-bars">
        {d.rows.map((r, i) => {
          const share = d.totals?.costUSD ? Math.round((r.costUSD / d.totals.costUSD) * 100) : 0;
          return (
            <div className="gov-bar-row" key={i}>
              <div className="gov-bar-head">
                <span className="gov-bar-label" title={r.route || r.adminEmail}>
                  {isAdmin ? r.adminEmail : r.label}
                </span>
                <span className="gov-bar-val">{usd(r.costUSD)}<em>{share}%</em></span>
              </div>
              <div className="gov-bar-track">
                <div className="gov-bar-fill" style={{ width: `${(r.costUSD / max) * 100}%` }} />
              </div>
              <div className="gov-bar-meta">
                {num(r.calls)} call{r.calls === 1 ? '' : 's'}
                {r.totalTokens ? ` · ${num(r.totalTokens)} tokens` : ''}
                {r.avgCostPerCall ? ` · $${r.avgCostPerCall} avg` : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SeriesCard = ({ d }) => {
  if (!d?.series?.length) return null;
  const max = Math.max(...d.series.map(s => s.costUSD), 0.0001);
  return (
    <div className="gov-card">
      <div className="gov-card-title">Daily spend · {d.window}</div>
      <div className="gov-spark">
        {d.series.map((s, i) => (
          <div
            className={`gov-spark-col${s.date === d.peak?.date && s.costUSD > 0 ? ' peak' : ''}${!s.calls ? ' empty' : ''}`}
            key={i}
            title={s.calls ? `${s.date} — ${usd(s.costUSD)} over ${s.calls} call${s.calls === 1 ? '' : 's'}` : `${s.date} — no activity`}
          >
            <div className="gov-spark-bar" style={{ height: `${Math.max(3, (s.costUSD / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="gov-spark-axis">
        <span>{fmtDay(d.series[0]?.date)}</span>
        <span>{fmtDay(d.series[d.series.length - 1]?.date)}</span>
      </div>
      {d.peak && (
        <div className="gov-note">
          Peak: {fmtDay(d.peak.date)} at {usd(d.peak.costUSD)}
        </div>
      )}
    </div>
  );
};

const TopCallsCard = ({ d }) => {
  if (!d?.rows?.length) return null;
  return (
    <div className="gov-card">
      <div className="gov-card-title">Most expensive calls · {d.window}</div>
      <div className="gov-list">
        {d.rows.map((r, i) => (
          <div className="gov-list-row" key={i}>
            <div className="gov-list-main">
              <div className="gov-list-title">{r.label}</div>
              <div className="gov-list-meta">
                {fmtDate(r.ts)} · {r.adminEmail} · {num(r.totalTokens)} tokens
              </div>
            </div>
            <div className="gov-list-val">{usd(r.costUSD)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const GuardrailsCard = ({ d }) => {
  if (!d) return null;
  const sev = { critical: '⛔', warning: '⚠️', info: 'ℹ️' };
  return (
    <div className="gov-card">
      <div className="gov-card-title">Guardrail activations · {d.window}</div>
      {d.total === 0 ? (
        <div className="gov-empty">No activations recorded for this period.</div>
      ) : (
        <div className="gov-list">
          {d.events.slice(0, 20).map((e, i) => (
            <div className={`gov-list-row gov-sev-${e.severity}`} key={i}>
              <div className="gov-list-main">
                <div className="gov-list-title">
                  {sev[e.severity] || '•'} {String(e.type).replace(/_/g, ' ')}
                </div>
                <div className="gov-list-meta">{e.detail}</div>
                <div className="gov-list-meta gov-dim">{fmtDate(e.at)} · {e.source}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {d.sessionEventsCaveat && <div className="gov-note">{d.sessionEventsCaveat}</div>}
    </div>
  );
};

const PostureCard = ({ d }) => {
  if (!d) return null;
  return (
    <div className="gov-card">
      <div className="gov-card-title">Budget posture · {d.window}</div>
      <div className="gov-grid">
        <div><span>{usd(d.avgDailyCostUSD)}</span>avg / day</div>
        <div><span>{usd(d.peakDayCostUSD)}</span>peak day</div>
        <div><span>{d.peakPctOfBudget}%</span>peak of budget</div>
        <div><span>{d.daysTracked}</span>days tracked</div>
        <div><span>{d.daysOver50Pct}</span>days over 50%</div>
        <div><span>{d.daysOver80Pct}</span>days over 80%</div>
        <div className={d.daysHardKilled ? 'gov-bad' : ''}>
          <span>{d.daysHardKilled}</span>hard kills
        </div>
        <div><span>${d.dailyBudgetUSD}</span>daily budget</div>
      </div>
    </div>
  );
};

const MonthlyReportCard = ({ d, onCopy }) => {
  if (!d) return null;
  const late = d.period?.complete && !d.deadline?.withinDeadline;
  return (
    <div className="gov-card gov-report">
      <div className="gov-report-head">
        <div>
          <div className="gov-report-title">{d.title}</div>
          <div className="gov-sub">{d.reportRef} · generated {fmtDate(d.generatedAt)}</div>
        </div>
        <span className={`gov-chip ${late ? 'gov-chip-bad' : d.period?.complete ? 'gov-chip-good' : 'gov-chip-info'}`}>
          {d.period?.complete
            ? (late ? 'Overdue' : `Day ${d.deadline?.businessDaysSinceMonthEnd} of 5`)
            : 'Interim'}
        </span>
      </div>

      <div className="gov-grid gov-grid-3">
        <div><span>{usd(d.totals?.costUSD)}</span>total cost</div>
        <div><span>{num(d.totals?.calls)}</span>calls</div>
        <div><span>{num(d.totals?.totalTokens)}</span>tokens</div>
      </div>

      <div className="gov-report-section">
        <div className="gov-report-h">Summary</div>
        {(d.narrative || []).map((line, i) => (
          <p className="gov-report-p" key={i}>{line}</p>
        ))}
      </div>

      {!!d.spendByRoute?.length && (
        <div className="gov-report-section">
          <div className="gov-report-h">Top cost drivers</div>
          <table className="gov-table">
            <thead>
              <tr><th>Feature</th><th>Cost</th><th>Calls</th><th>Tokens</th></tr>
            </thead>
            <tbody>
              {d.spendByRoute.slice(0, 8).map((r, i) => (
                <tr key={i}>
                  <td>{r.label}</td>
                  <td>{usd(r.costUSD)}</td>
                  <td>{num(r.calls)}</td>
                  <td>{num(r.totalTokens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="gov-report-section">
        <div className="gov-report-h">
          Guardrail activations ({d.guardrails?.total ?? 0})
        </div>
        {d.guardrails?.total ? (
          <table className="gov-table">
            <thead><tr><th>Date</th><th>Type</th><th>Detail</th></tr></thead>
            <tbody>
              {d.guardrails.events.slice(0, 12).map((e, i) => (
                <tr key={i}>
                  <td>{e.date}</td>
                  <td>{String(e.type).replace(/_/g, ' ')}</td>
                  <td>{e.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="gov-empty">None recorded for the period.</div>
        )}
      </div>

      <div className="gov-report-section">
        <div className="gov-report-h">Basis of preparation</div>
        <ul className="gov-basis">
          {(d.basis || []).map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      </div>

      <button className="gov-copy-btn" onClick={() => onCopy(d)}>
        📋 Copy report text
      </button>
    </div>
  );
};

const HelpCard = ({ d, onPick }) => {
  if (!d?.examples?.length) return null;
  return (
    <div className="gov-card">
      <div className="gov-card-title">Try asking</div>
      <div className="gov-examples">
        {d.examples.map((ex, i) => (
          <button className="gov-example" key={i} onClick={() => onPick(ex)}>{ex}</button>
        ))}
      </div>
    </div>
  );
};

// ── plain-text rendering of a report, for the copy button ───────────────────
function reportToText(d) {
  const L = [];
  L.push(d.title);
  L.push(`Reference: ${d.reportRef}`);
  L.push(`Generated: ${fmtDate(d.generatedAt)}`);
  L.push('');
  L.push('SUMMARY');
  (d.narrative || []).forEach(n => L.push(`  ${n}`));
  L.push('');
  L.push('TOP COST DRIVERS');
  (d.spendByRoute || []).slice(0, 10).forEach(r => {
    L.push(`  ${r.label} — ${usd(r.costUSD)} (${num(r.calls)} calls, ${num(r.totalTokens)} tokens)`);
  });
  L.push('');
  L.push(`GUARDRAIL ACTIVATIONS (${d.guardrails?.total ?? 0})`);
  if (d.guardrails?.total) {
    d.guardrails.events.forEach(e => {
      L.push(`  ${e.date} — ${String(e.type).replace(/_/g, ' ')}: ${e.detail}`);
    });
  } else {
    L.push('  None recorded for the period.');
  }
  L.push('');
  L.push('BUDGET POSTURE');
  const p = d.budgetPosture || {};
  L.push(`  Daily budget: $${p.dailyBudgetUSD}`);
  L.push(`  Average daily spend: ${usd(p.avgDailyCostUSD)}`);
  L.push(`  Peak day: ${usd(p.peakDayCostUSD)} (${p.peakPctOfBudget}% of budget)`);
  L.push(`  Days over 50% / 80% / hard kill: ${p.daysOver50Pct} / ${p.daysOver80Pct} / ${p.daysHardKilled}`);
  L.push('');
  L.push('BASIS OF PREPARATION');
  (d.basis || []).forEach(b => L.push(`  - ${b}`));
  return L.join('\n');
}

// ── main component ──────────────────────────────────────────────────────────
const GovernanceAgentChatLogic = ({ onClose }) => {
  const [messages, setMessages] = useState([{
    role: 'agent',
    type: 'help',
    message:
      "I'm the AI Governance agent. I report on token spend and guardrail activity from recorded usage data — I make no AI calls myself, so I cost nothing and stay available even during a budget hard kill.",
    data: {
      examples: QUICK_ACTIONS.map(q => q.prompt),
    },
  }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy]);

  const send = useCallback(async (text) => {
    const msg = String(text ?? input).trim();
    if (!msg || busy) return;

    setMessages(prev => [...prev, { role: 'user', message: msg }]);
    setInput('');
    setBusy(true);

    try {
      const res = await axios.post(
        `${API_URL}/admin/rpa/agent/governance/command`,
        { message: msg },
        { withCredentials: true }
      );
      const d = res.data || {};
      setMessages(prev => [...prev, {
        role: 'agent',
        type: d.type || 'text',
        message: d.message || 'No response.',
        data: d.data || null,
      }]);
    } catch (err) {
      const status = err.response?.status;
      const msgText =
        status === 403
          ? 'This agent is restricted to system administrators, since it exposes cross-project spend data.'
          : (err.response?.data?.message || err.response?.data?.error || err.message);
      setMessages(prev => [...prev, { role: 'agent', type: 'error', message: msgText }]);
    } finally {
      setBusy(false);
    }
  }, [input, busy]);

  const handleCopy = async (report) => {
    try {
      await navigator.clipboard.writeText(reportToText(report));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const renderCard = (m) => {
    switch (m.type) {
      case 'governance_status': return <StatusCard d={m.data} />;
      case 'spend_breakdown':   return <BreakdownCard d={m.data} />;
      case 'spend_series':      return <SeriesCard d={m.data} />;
      case 'top_calls':         return <TopCallsCard d={m.data} />;
      case 'guardrails':        return <GuardrailsCard d={m.data} />;
      case 'budget_posture':    return <PostureCard d={m.data} />;
      case 'monthly_report':    return <MonthlyReportCard d={m.data} onCopy={handleCopy} />;
      case 'help':              return <HelpCard d={m.data} onPick={send} />;
      default:                  return null;
    }
  };

  return (
    <div className="gov-chat">
      <div className="gov-chat-head">
        <div>
          <div className="gov-chat-title">🛡️ AI Governance Agent</div>
          <div className="gov-chat-sub">Read-only · no AI calls · system admin</div>
        </div>
        {onClose && <button className="gov-chat-close" onClick={onClose}>✕</button>}
      </div>

      <div className="gov-chat-body">
        {messages.map((m, i) => (
          <div className={`gov-msg gov-msg-${m.role}`} key={i}>
            {m.role === 'user'
              ? <div className="gov-bubble gov-bubble-user">{m.message}</div>
              : (
                <div className="gov-bubble gov-bubble-agent">
                  <div className={m.type === 'error' ? 'gov-err-text' : ''}>{m.message}</div>
                  {renderCard(m)}
                </div>
              )}
          </div>
        ))}
        {busy && (
          <div className="gov-msg gov-msg-agent">
            <div className="gov-bubble gov-bubble-agent gov-typing">
              <span /><span /><span />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="gov-quick">
        {QUICK_ACTIONS.map((q, i) => (
          <button key={i} disabled={busy} onClick={() => send(q.prompt)}>{q.label}</button>
        ))}
      </div>

      <form
        className="gov-chat-input"
        onSubmit={(e) => { e.preventDefault(); send(); }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about AI spend or guardrails…"
          disabled={busy}
        />
        <button type="submit" disabled={busy || !input.trim()}>Send</button>
      </form>

      {copied && <div className="gov-toast">Report text copied</div>}
    </div>
  );
};

export default GovernanceAgentChatLogic;